import LayoutAccount from '@components/common/LayoutAccount'
import React, { useCallback, useRef, useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import axios from '@services/axiosInterceptorInstance'
import {
  EmptyGuid,
  NEXT_GET_ALL_STORES,
  NEXT_GOOGLE_AUTOCOMPLETE_API,
  NEXT_PLACE_DETAILs_API,
} from '@components/utils/constants'
import withDataLayer from '@components/withDataLayer'
import { useRouter } from 'next/router'
import LoadingDots from '@components/ui/LoadingDots'
import { useGoogleAnalyticsEvent } from 'hooks/useGAEvent'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { useUI } from '@components/ui'
import { useDebounce } from 'hooks/useDebounce'
import { LocationPopup } from '@components/Store-Locator/Location-popup'
import { IStore } from '@components/utils/model/store.model'
import { IDeviceInfo } from '../../components/ui/context'
import { StoreList } from '../../components/Store-Locator/store-list'
import { SubHeader } from '@components/Store-Locator/sub-header'
import { StoreHead } from '@components/Store-Locator/head'
import { StoreSearch } from '@components/Store-Locator/search'
import { PagePropType, getPagePropType } from '@framework/page-props'
import { IPagePropsProvider } from '@framework/contracts/page-props/IPagePropsProvider'
import useEventAnalytics from '@components/services/analytics/useEventAnalytics'
import { AnalyticsEventType } from '@components/services/analytics'
const StoreMap = dynamic(
  () => import('../../components/Store-Locator/store-map')
)

const DEBOUNCE_TIMER = 300

interface IStoreProps {
  deviceInfo: IDeviceInfo
  previousPath: string
}

function Store({ deviceInfo, previousPath }: IStoreProps) {
  const [stores, setStores] = useState([])
  const [filteredStores, setFilteredStores]: any = useState([])
  const router = useRouter()
  const [inputValue, setInputValue] = useState('')
  const { isMobile, isIPadorTablet } = deviceInfo
  const [map, setMap]: any = useState(null)
  const [isOpenInfoWindow, setIsOpenInfoWindow] = useState(false)
  const [infoWindowData, setInfoWindowData]: any = useState({
    id: '',
    store: {},
  })
  const [isLocationDialog, setLocationDialog] = useState(true)
  const [isDataLoading, setDataLoading] = useState(true)
  const [isSearchDataLoading, setSearchLoader] = useState(false)
  const [currentLatLng, setCurrentLatLng] = useState({ lat: 0, lng: 0 })
  const [currentTab, setCurrentTab] = useState('all')
  const [loadingDots, setLoadingDots] = useState('')
  const [tabsCount, setTabsCount] = useState({ all: 0, impact: 0, ebo: 0 })
  const [autoCompleteList, setAutoCompleteList] = useState([])
  const [isErrorMsg, setErrorMsg] = useState(false)
  const searchPincodeRef = useRef(null)
  const trackGoogleAnalyticsEvent = useGoogleAnalyticsEvent()
  const { recordAnalytics } = useEventAnalytics()
  const { user, cartItems } = useUI()

  let deviceCheck = ''
  if (isMobile || isIPadorTablet) {
    deviceCheck = 'Mobile'
  } else {
    deviceCheck = 'Desktop'
  }

  useEffect(() => {
    const pincode: any = router?.query?.['pincode']
    const type = router?.query?.['type']
    let filteredStore = stores
    const regex = /[^0-9]/
    if (pincode && !regex.test(pincode) && pincode?.length == 6) {
      filteredStore = filteredStore?.filter(({ postCode }: IStore) =>
        postCode.includes(pincode)
      )
      setInputValue(pincode)
    }
    if (type && type == 'exclusive') {
      setCurrentTab('EBO')
      filteredStore = filteredStore.filter(
        (store: IStore) => store?.customInfo7?.toUpperCase() == currentTab
      )
    } else if (type && type == 'preferred-partner') {
      setCurrentTab('IMPACT')
      filteredStore = filteredStore.filter(
        (store: IStore) => store?.customInfo7?.toUpperCase() == currentTab
      )
    }
    setFilteredStores(filteredStore)
  }, [router?.query, stores])

  useEffect(() => {
    if (map && inputValue && filteredStores?.length) {
      const bounds = new window.google.maps.LatLngBounds()
      for (let i = 0; i < filteredStores.length; i++) {
        bounds.extend(
          new window.google.maps.LatLng(
            filteredStores[i]?.lat,
            filteredStores[i]?.lng
          )
        )
      }
      map.fitBounds(bounds)
      const findZoom = isMobile ? 10 : 12
      map.setZoom(findZoom)
    }
  }, [filteredStores, map])

  useEffect(() => {
    if (isLocationDialog) {
      document.documentElement.style.paddingRight = '0px'
    } else {
      document.documentElement.style.paddingRight = '0px'
    }
    return () => {
      document.documentElement.style.paddingRight = '0px'
    }
  }, [isLocationDialog])

  const getAllStore = async () => {
    try {
      const { data }: any = await axios.get(NEXT_GET_ALL_STORES)
      let stores = data?.map((store: IStore) => {
        return {
          ...store,
          lat: Number(store?.latitude),
          lng: Number(store?.longitude),
        }
      })
      stores = stores?.filter(
        (store: IStore) =>
          store?.customInfo7 !== '' && store?.customInfo7 !== null
      )
      setTabsCount({
        all: stores?.length,
        impact: stores?.filter(
          ({ customInfo7 }: IStore) => customInfo7?.toUpperCase() == 'IMPACT'
        )?.length,
        ebo: stores?.filter(
          ({ customInfo7 }: IStore) => customInfo7?.toUpperCase() == 'EBO'
        )?.length,
      })
      setFilteredStores(stores)
      setDataLoading(false)
      setStores(stores)
    } catch (error) {
      console.error('err in fetching stores', error)
    }
  }

  useEffect(() => {
    getAllStore()
    recordAnalytics(AnalyticsEventType.OTHER_PAGE, { type: 'other', data: { cartItems } })
  }, [])

  const getDirection = (lat: string, lng: string, fax: string) => {
    const url = fax
      ? fax
      : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const headerChangeGAEvent = (selectedTab: string) => {
    trackGoogleAnalyticsEvent(
      GA_EVENT.HEADER_CHANGE_STORE,
      {
        current_page: window?.location?.href,
        current_header: selectedTab,
        previous_header: currentTab,
        device: deviceCheck,
        loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
      },
      window
    )
  }

  const currentLocationGAEvent = () => {
    trackGoogleAnalyticsEvent(
      GA_EVENT.CURRENT_LOCATION,
      {
        device: deviceCheck,
        loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
      },
      window
    )
  }

  const chooseCityGAEvent = (city: string) => {
    trackGoogleAnalyticsEvent(
      GA_EVENT.CHOOSE_CITY,
      {
        selected_city: city,
        device: deviceCheck,
        loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
      },
      window
    )
  }

  const locationPermissionGAEvent = () => {
    trackGoogleAnalyticsEvent(
      GA_EVENT.LOCATION_PERMISSION,
      {
        current_page: window?.location?.href,
        device: deviceCheck,
        loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
      },
      window
    )
  }

  const onSearchChange = async (e: any, tab: string, allStores: IStore[]) => {
    try {
      const {
        data: { predictions },
      }: any = await axios.get(NEXT_GOOGLE_AUTOCOMPLETE_API, {
        params: { input: e.target.value },
      })
      if (predictions) {
        setAutoCompleteList(predictions)
      }
    } catch (e) {
      console.error(e)
    }

    if (e?.target?.value?.length == 0) {
      setCurrentLatLng({ lat: 0, lng: 0 })
      setAutoCompleteList([])

      let filterStore = allStores
      setTabsCount({
        all: allStores?.length,
        impact: allStores?.filter(
          ({ customInfo7 }: IStore) => customInfo7?.toUpperCase() == 'IMPACT'
        )?.length,
        ebo: allStores?.filter(
          ({ customInfo7 }: IStore) => customInfo7?.toUpperCase() == 'EBO'
        )?.length,
      })
      if (['EBO', 'IMPACT'].includes(tab)) {
        filterStore = allStores.filter(
          (store: IStore) => store?.customInfo7?.toUpperCase() == tab
        )
      }
      setFilteredStores(filterStore)
    }
  }

  const onSearchChangeDebounce = useCallback(
    useDebounce((e: any, tab: string, allStores: IStore[]) => {
      onSearchChange(e, tab, allStores)
    }, DEBOUNCE_TIMER),
    []
  )

  const openStoreDetails = (store: IStore) => {
    localStorage.setItem('storeDetails', JSON.stringify(store))
    router.push(`/stores/${store?.storeCode}`)
  }

  const handleMarkerClick = (id: string, store: IStore) => {
    setInfoWindowData({ id, store })
    setIsOpenInfoWindow(true)
  }

  useEffect(() => {
    let filteredStore = stores
    const { lat, lng } = currentLatLng

    if (currentTab == 'all') {
      if (lat && lng) {
        setDistanceAndSortStore(lat, lng, filteredStore, false)
        return
      }
      setFilteredStores(filteredStore)
      return
    }
    filteredStore = stores.filter(
      (store: IStore) => store?.customInfo7?.toUpperCase() == currentTab
    )

    if (lat && lng) {
      setDistanceAndSortStore(lat, lng, filteredStore, false)
      return
    }
    setFilteredStores(filteredStore)
  }, [currentTab])

  const mapNearestStore = (lat1: number, lng1: number) => {
    stores?.map((store: IStore) => {
      const distance = getDistanceTwoLatLng(
        store?.latitude,
        store?.longitude,
        lat1,
        lng1
      )
      let getStore: IStore | any = stores.find(
        ({ id }: IStore) => id == store?.id
      )
      if (getStore) {
        getStore['distance'] = distance
      }
    })
    let filterStore = stores
      .filter((store: IStore) => store?.distance < 30)
      ?.sort(
        (a: IStore, b: IStore) =>
          parseFloat(a?.distance) - parseFloat(b?.distance)
      )
    setTabsCount({
      all: filterStore?.length,
      impact: filterStore?.filter(
        ({ customInfo7 }: IStore) => customInfo7?.toUpperCase() == 'IMPACT'
      )?.length,
      ebo: filterStore?.filter(
        ({ customInfo7 }: IStore) => customInfo7?.toUpperCase() == 'EBO'
      )?.length,
    })
    if (['EBO', 'IMPACT'].includes(currentTab)) {
      filterStore = filterStore.filter(
        (store: IStore) => store?.customInfo7?.toUpperCase() == currentTab
      )
    }
    setFilteredStores(filterStore)
    setSearchLoader(false)
    setAutoCompleteList([])
  }

  const selectPlace = async (placeId: string) => {
    try {
      const {
        data: {
          result: {
            geometry: { location },
          },
        },
      }: any = await axios.get(NEXT_PLACE_DETAILs_API, {
        params: { placeId },
      })
      setSearchLoader(true)
      setCurrentLatLng({ lat: location?.lat, lng: location?.lng })
      mapNearestStore(location?.lat, location?.lng)
    } catch (error) {
      setSearchLoader(false)
      console.error(error)
    }
  }

  const searchInputButton = () => {
    if (inputValue.length) {
      searchPincodeRef.current = null
      setInputValue('')
      let filteredStore = stores
      if (['EBO', 'IMPACT'].includes(currentTab)) {
        filteredStore = filteredStore.filter(
          (store: IStore) => store?.customInfo7?.toUpperCase() == currentTab
        )
      }
      setFilteredStores(filteredStore)
      setAutoCompleteList([])
      setTabsCount({
        all: stores?.length,
        impact: stores?.filter(
          ({ customInfo7 }: IStore) => customInfo7?.toUpperCase() == 'IMPACT'
        )?.length,
        ebo: stores?.filter(
          ({ customInfo7 }: IStore) => customInfo7?.toUpperCase() == 'EBO'
        )?.length,
      })
      setCurrentLatLng({ lat: 0, lng: 0 })
    } else {
      setLocationDialog(true)
    }
  }

  const getSubHeader = () => {
    return (
      <SubHeader
        isDataLoading={isDataLoading}
        isMobile={isMobile}
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        headerChangeGAEvent={headerChangeGAEvent}
        tabsCount={tabsCount}
      />
    )
  }

  const successCallback = (position: any) => {
    const latlng: google.maps.LatLng = new google.maps.LatLng(
      position.coords.latitude,
      position.coords.longitude
    )
    setCurrentLatLng({
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    })
    const geocoder = new google.maps.Geocoder()
    geocoder.geocode({ location: latlng }, function (results: any, status) {
      if (status == google.maps.GeocoderStatus.OK) {
        if (results[1]) {
          results.forEach((addressComponent: any) => {
            if (addressComponent.types[0] == 'locality') {
              const itemLocality =
                addressComponent.address_components[0].long_name
              locationPermissionGAEvent()
              setInputValue(itemLocality)
              setLoadingDots('')
              setLocationDialog(false)
              setDistanceAndSortStore(
                position.coords.latitude,
                position.coords.longitude,
                stores,
                true
              )
            }
          })
        }
      }
    })
  }

  const errorCallback = (error: any) => {
    setLoadingDots('')
    setErrorMsg(true)
    console.error(error)
  }

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(successCallback, errorCallback)
    }
  }

  const getDistanceTwoLatLng = (
    lat2: number,
    lng2: number,
    lat1: number,
    lng1: number
  ) => {
    const r = 6371 // km
    const p = Math.PI / 180
    if (lat1 && lng1) {
      const a =
        0.5 -
        Math.cos((lat2 - lat1) * p) / 2 +
        (Math.cos(lat1 * p) *
          Math.cos(lat2 * p) *
          (1 - Math.cos((lng2 - lng1) * p))) /
        2
      return (2 * r * Math.asin(Math.sqrt(a))).toFixed(1)
    }
    return
  }

  const setDistanceAndSortStore = (
    lat1: number,
    lng1: number,
    filteredStore: IStore[],
    updateCount: boolean
  ) => {
    filteredStore?.map((store: IStore) => {
      const distance = getDistanceTwoLatLng(
        store?.latitude,
        store?.longitude,
        lat1,
        lng1
      )
      let getStore: IStore | undefined = filteredStore.find(
        ({ id }: IStore) => id == store?.id
      )
      if (getStore) {
        getStore['distance'] = distance
      }
    })
    filteredStore = filteredStore
      .filter((store: any) => store?.distance < 30)
      ?.sort(
        (a: any, b: any) => parseFloat(a?.distance) - parseFloat(b?.distance)
      )
    if (updateCount) {
      setTabsCount({
        all: filteredStore?.length,
        impact: filteredStore?.filter(
          ({ customInfo7 }: any) => customInfo7?.toUpperCase() == 'IMPACT'
        )?.length,
        ebo: filteredStore?.filter(
          ({ customInfo7 }: any) => customInfo7?.toUpperCase() == 'EBO'
        )?.length,
      })
    }
    setFilteredStores(filteredStore)
  }

  const getMap = () => {
    return (
      <StoreMap
        currentLatLng={currentLatLng}
        filteredStores={filteredStores}
        handleMarkerClick={handleMarkerClick}
        getDirection={getDirection}
        isOpenInfoWindow={isOpenInfoWindow}
        infoWindowData={infoWindowData}
        setIsOpenInfoWindow={setIsOpenInfoWindow}
        setMap={setMap}
      />
    )
  }

  return (
    <>
      <StoreHead
        getSubHeader={getSubHeader}
        previousPath={previousPath}
        deviceInfo={deviceInfo}
      />
      <div
        className={`${isMobile ? '' : 'flex justify-around flex-row store-container'
          } ${isDataLoading ? 'animate-pulse' : ''}`}
      >
        <div
          className={`${isMobile ? 'px-[20px]' : 'flex-1 basis-[18%] pl-[20px] pr-[25px]'
            }`}
        >
          {isMobile ? '' : getSubHeader()}
          <StoreSearch
            isDataLoading={isDataLoading}
            isMobile={isMobile}
            searchPincodeRef={searchPincodeRef}
            inputValue={inputValue}
            setInputValue={setInputValue}
            onSearchChangeDebounce={onSearchChangeDebounce}
            searchInputButton={searchInputButton}
            autoCompleteList={autoCompleteList}
            selectPlace={selectPlace}
            currentTab={currentTab}
            stores={stores}
          />
          {/* map for mobile*/}
          {isMobile ? (
            <div className="h-[320px] mb-[20px]">{getMap()}</div>
          ) : (
            ''
          )}
          {/* stores list */}
          <StoreList
            isDataLoading={isDataLoading}
            filteredStores={filteredStores}
            isMobile={isMobile}
            currentLatLng={currentLatLng}
            getDirection={getDirection}
            openStoreDetails={openStoreDetails}
            setLoadingDots={setLoadingDots}
            loadingDots={loadingDots}
          />
        </div>
        {/* map for web */}
        {!isMobile ? (
          <div className="h-[500px] flex-1 basis-[50%] desktop-laptop pt-[50px]">
            {getMap()}
          </div>
        ) : (
          ''
        )}
      </div>

      {isSearchDataLoading ? (
        <div className="fixed top-2 w-full h-full opacity-50 bg-white flex justify-center items-center">
          <LoadingDots />
        </div>
      ) : (
        ''
      )}
      {/* location */}
      <LocationPopup
        isLocationDialog={isLocationDialog}
        setLocationDialog={setLocationDialog}
        getUserLocation={getUserLocation}
        setLoadingDots={setLoadingDots}
        loadingDots={loadingDots}
        isErrorMsg={isErrorMsg}
        setErrorMsg={setErrorMsg}
        deviceCheck={deviceCheck}
      />
    </>
  )
}

Store.Layout = LayoutAccount
export async function getServerSideProps() {
  const props: IPagePropsProvider = getPagePropType({ type: PagePropType.COMMON }) as IPagePropsProvider
  const commonPageProps = await props.getPageProps({ cookies: {} })
  return {
    props: {
      ...commonPageProps,
    }, // will be passed to the page component as props
  }
}
export default withDataLayer(Store, '', true, LayoutAccount)
